import { Link } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { Button, Icon } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useAuth } from "@/hooks/useAuth";
import { useCanShop } from "@/hooks/useCanShop";
import { useSignOut } from "@/hooks/useSignOut";
import { signInRedirect } from "@components/Auth/signInRedirect";
import { currentUserAtom } from "@/store/userAtoms";
import { isSearchPanelOpenAtom } from "@customer/Search/store/searchAtoms";
import { ShipToSwitcher } from "./_components/ShipToSwitcher";

interface DesktopNavProps {
    cartCount: number;
}

export const DesktopNav = ({ cartCount }: DesktopNavProps) => {
    const { isAuthenticated, instance } = useAuth();
    const appUser = useAtomValue(currentUserAtom);
    const openSearchPanel = useSetAtom(isSearchPanelOpenAtom);
    const signOut = useSignOut();

    // Not-yet-approved users are walled at /c/pending; hide the shopping entry
    // points and keep the logo on the wall so clicking it doesn't bounce.
    const canShop = useCanShop();

    const handleLogin = () => {
        signInRedirect(instance);
    };

    if (!isAuthenticated) {
        return (
            <div
                data-testid="desktop-nav"
                className="hidden md:flex w-full items-center gap-6"
            >
                <Link
                    to={ROUTES.HOME}
                    aria-label="USNARA home"
                    className="flex items-center"
                >
                    <img
                        src="/logo/Logotype-Color.svg"
                        alt=""
                        className="h-10 w-auto"
                    />
                </Link>
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleLogin}
                        className="btn btn-ghost text-base font-bold normal-case text-secondary-content"
                    >
                        Sign In
                    </button>
                    {/* TODO: /apply arrives in E5-S4 (#191) */}
                    <Button
                        to={ROUTES.APPLY}
                        color={ComponentColor.Warning}
                        className="text-base text-neutral font-bold normal-case"
                    >
                        Apply Now
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="desktop-nav"
            className="hidden md:flex w-full items-center gap-6"
        >
            <Link
                to={canShop ? ROUTES.BROWSE : ROUTES.PENDING}
                aria-label="USNARA home"
                className="flex items-center"
            >
                <img
                    src="/logo/Logotype-Color.svg"
                    alt=""
                    className="h-10 w-auto"
                />
            </Link>
            <div className="dropdown">
                <button
                    tabIndex={0}
                    type="button"
                    className="btn btn-ghost text-base font-bold normal-case gap-2"
                >
                    <Icon
                        name="account_circle"
                        filled
                        className="text-2xl"
                    />
                    Account
                    <Icon
                        name="expand_more"
                        className="text-base text-accent"
                    />
                </button>
                <ul
                    tabIndex={0}
                    data-testid="account-menu"
                    className="dropdown-content menu bg-base-100 text-neutral rounded-box z-10 mt-2 w-52 p-2 shadow"
                >
                    {appUser && (
                        <li className="px-2 py-1 opacity-70">
                            {appUser.firstName} {appUser.lastName}
                        </li>
                    )}
                    <li>
                        <Link to={ROUTES.ACCOUNT}>
                            <Icon name="person" className="text-base" />
                            My Account
                        </Link>
                    </li>
                    <li>
                        <button type="button" onClick={signOut}>
                            <Icon name="logout" className="text-base" />
                            Logout
                        </button>
                    </li>
                </ul>
            </div>
            <div className="flex-1" />
            {canShop && (
                <div className="flex items-center gap-2">
                    {/* No desktop mockup exists for this control (the PNG is SharePoint-only),
                        so the mobile lockup is mirrored into the action cluster — leading it,
                        because it is context for Search and Cart rather than an action of its
                        own. Correct freely once the design lands. */}
                    <ShipToSwitcher />
                    <div className="dropdown dropdown-end">
                        <button
                            tabIndex={0}
                            type="button"
                            className="btn btn-ghost text-base font-bold normal-case gap-1"
                        >
                            Products
                            <Icon
                                name="expand_more"
                                className="text-base text-accent"
                            />
                        </button>
                        <ul
                            tabIndex={0}
                            className="dropdown-content menu bg-base-100 text-neutral rounded-box z-10 mt-2 w-52 p-2 shadow"
                        >
                            <li>
                                <span className="opacity-60">Coming soon</span>
                            </li>
                        </ul>
                    </div>
                    <button
                        type="button"
                        onClick={() => openSearchPanel(true)}
                        className="btn btn-ghost text-base font-bold normal-case gap-2"
                    >
                        <Icon name="search" className="text-2xl" />
                        Search
                    </button>
                    <Link
                        to={ROUTES.CART}
                        className="btn btn-ghost text-base font-bold normal-case gap-2 relative"
                    >
                        <Icon name="shopping_cart" className="text-2xl" />
                        Cart
                        {cartCount > 0 && (
                            <span
                                data-testid="cart-badge"
                                className="badge badge-warning badge-sm absolute -top-1 -right-1"
                            >
                                {cartCount}
                            </span>
                        )}
                    </Link>
                </div>
            )}
        </div>
    );
};
